import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import BottomNav from '../components/BottomNav';
import { getParasha, getTradition, getAliyaLatoraDate } from '../utils/userSelections';
import { getHaftaraTitle } from '../utils/haftarot';
import { parseDateString, formatHebrewWithThousands } from '../utils/parashaCalc';
import { useTraditionRabbi } from '../utils/rabbiesFirestore';
import { computeAggregates, Aggregates, loadScores } from '../utils/progressStorage';
import { trackClick, trackNavigation, trackPageView } from '../utils/analytics';
import cloudsImg from '../assets/webp/clouds.webp';
import student1Img from '../assets/webp/student1.webp';
import familyImage from '../assets/webp/familiy-1.webp';
import playWaveIcon from '../assets/webp/play1.webp';
import SelfTestCard from '../components/SelfTestCard';
// import yamen1Img from "../assets/webp/yamen1.webp";
import MyParashaSection from '../components/MyParashaSection';
import TrueFactsCarousel from '../components/TrueFactsCarousel';
import AcademyCarousel from '../components/AcademyCarousel';
import UserHeader from '../components/UserHeader';
import { ReviewRequest, subscribeToStudentReviewRequests } from '../utils/reviewRequests';
import { formatNumberForLocale } from '../utils/hebrewNumbers';

// Color constants (matching AppOnboardingPage theme)
const BLUE_ACCENT = '#3B7BF4';
const TEXT_DARK = '#1A1A2E';
const TEXT_SECONDARY = '#6B7280';
const BORDER_LIGHT = '#D1D5DB';

type StoredUser = {
  userId?: string | null;
  email: string | null;
  name?: string | null;
  displayName?: string | null;
  photoURL: string | null;
};

const clampPercent = (val: number) => Math.max(0, Math.min(100, val));

const UserPortalPage: React.FC = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const isHebrew = i18n.language?.startsWith('he');
  const [agg, setAgg] = useState<Aggregates | null>(null);
  const [user, setUser] = useState<StoredUser | null>(null);
  const [reviewRequests, setReviewRequests] = useState<ReviewRequest[]>([]);
  const [selectedReview, setSelectedReview] = useState<ReviewRequest | null>(null);
  const [showConnectionSuccess, setShowConnectionSuccess] = useState<boolean>(
    () => !!(location.state as { connectionSuccess?: boolean } | null)?.connectionSuccess,
  );
  const connectedName: string =
    (location.state as { connectedName?: string } | null)?.connectedName || '';

  const parasha = getParasha();
  const rabbi = useTraditionRabbi('parasha');

  useEffect(() => {
    const loadData = () => {
      setAgg(computeAggregates());
      try {
        const raw = localStorage.getItem('loggedInUser');
        setUser(raw ? (JSON.parse(raw) as StoredUser) : null);
      } catch {
        setUser(null);
      }
    };

    trackPageView('UserPortal');
    loadData();

    // Reload data when page becomes visible (e.g., navigating back)
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        loadData();
      }
    };

    // Also reload on window focus (for navigation within the app)
    const handleFocus = () => {
      loadData();
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('focus', handleFocus);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', handleFocus);
    };
  }, []);

  // Subscribe to student's review requests
  useEffect(() => {
    let studentId = '';
    try {
      const raw = localStorage.getItem('loggedInUser');
      if (raw) {
        const userData = JSON.parse(raw);
        studentId = userData.userId || userData.uid || userData.email || '';
      }
    } catch {
      // ignore
    }

    if (!studentId) return;

    const unsubscribe = subscribeToStudentReviewRequests(studentId, (requests) => {
      setReviewRequests(requests);
    });

    return () => unsubscribe();
  }, []);

  useEffect(() => {
    if (!showConnectionSuccess) return;
    const timer = setTimeout(() => setShowConnectionSuccess(false), 4000);
    return () => clearTimeout(timer);
  }, [showConnectionSuccess]);

  const progressPercent = useMemo(() => {
    const targetVerses = 20;
    const total = agg?.totalVerses || 0;
    return clampPercent(Math.round((total / targetVerses) * 100));
  }, [agg]);

  const parashaLabel = parasha
    ? isHebrew
      ? parasha.hebrew
      : parasha.english
    : (t('user_portal_parasha_placeholder', 'Select a parasha') as string);

  const rabbiName = isHebrew
    ? rabbi?.nameHe || rabbi?.nameEn || rabbi?.initials
    : rabbi?.nameEn || rabbi?.nameHe || rabbi?.initials;
  const rabbiNameDisplay = rabbiName || (t('user_portal_no_rabbi', 'Choose a rabbi') as string);

  const handlePractice = () => {
    trackClick('Enter Learning Mode', 'UserPortal');
    trackNavigation('UserPortal', '/parasha-overview', 'cta');
    navigate('/parasha-overview');
  };

  // const handleAskRabbi = () => {
  //   trackClick("Ask Rabbi", "UserPortal");
  //   trackNavigation("UserPortal", "/profile", "cta");
  //   navigate("/chooserabbi");
  // };

  const todaysMinutes = useMemo(() => {
    const scores = loadScores();
    if (!scores.length) return 0;
    const todayKey = new Date().toISOString().slice(0, 10);
    const seconds = scores
      .filter((s) => s.dateISO?.slice(0, 10) === todayKey)
      .reduce((sum, s) => sum + (s.durationSec || 0), 0);
    return Math.round(seconds / 60);
  }, []);

  const masteredCount = agg?.totalVerses ?? 0;
  const practiceMinutes = todaysMinutes || Math.round((agg?.hoursLearned || 0) * 60);

  const greetingName = useMemo(() => {
    try {
      const raw = localStorage.getItem('loggedInUser');
      if (raw) {
        const u = JSON.parse(raw);
        return (
          (u.displayName || '').split(' ')[0] || (t('user_portal_student', 'Student') as string)
        );
      }
    } catch {
      // ignore
    }
    return t('user_portal_student', 'Student') as string;
  }, [t]);

  const direction = 'rtl';

  const traditionKey = getTradition()?.key ?? null;

  const haftaraTitle = useMemo(
    () => (parasha ? getHaftaraTitle(parasha.english, traditionKey) : null),
    [parasha, traditionKey],
  );

  const { barMitzvaDate, barMitzvaDateLabel, hebrewDateLabel } = useMemo(() => {
    const raw = getAliyaLatoraDate();
    if (!raw) return { barMitzvaDate: null, barMitzvaDateLabel: null, hebrewDateLabel: null };
    const d = parseDateString(raw);
    if (!d) return { barMitzvaDate: null, barMitzvaDateLabel: null, hebrewDateLabel: null };
    return {
      barMitzvaDate: d,
      barMitzvaDateLabel: `${d.getDate()}.${String(d.getMonth() + 1).padStart(2, '0')}.${d.getFullYear()}`,
      hebrewDateLabel: formatHebrewWithThousands(d),
    };
  }, []);

  const initials = (name: string | null) => {
    if (!name) return 'D';
    const parts = name.trim().split(' ');
    if (parts.length >= 2) return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
    return name[0].toUpperCase();
  };

  const rabbiAvatar = () => {
    if (rabbi?.imageUrl) {
      return (
        <img
          src={rabbi.imageUrl}
          alt={rabbiNameDisplay}
          style={{
            width: '100%',
            height: '100%',
            borderRadius: '50%',
            objectFit: 'cover',
          }}
          onError={(e) => {
            (e.currentTarget as HTMLImageElement).style.display = 'none';
          }}
        />
      );
    }
    return (
      <div
        style={{
          width: '100%',
          height: '100%',
          borderRadius: '50%',
          background: BLUE_ACCENT,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: 'white',
          fontWeight: 700,
          fontSize: 24,
          border: `3px solid ${BLUE_ACCENT}`,
        }}
      >
        {initials(rabbiName || 'R')}
      </div>
    );
  };
  void rabbiAvatar;
  return (
    <div
      style={{
        flex: 1,
        minHeight: 0,
        overflow: 'hidden',
        backgroundColor: '#f9fafb',
        backgroundImage: `url(${cloudsImg})`,
        backgroundRepeat: 'no-repeat',
        backgroundSize: '100% auto',
        backgroundPosition: 'top center',
        fontFamily: "var(--kol-font-sans, 'Inter', system-ui, sans-serif)",
        color: TEXT_DARK,
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <div
        style={{
          flex: 1,
          minHeight: 0,
          display: 'flex',
          flexDirection: 'column',
          overflowY: 'auto',
          overflowX: 'hidden',
        }}
      >
        <UserHeader showBackButton={false} />
        <div
          style={{
            padding: '18px',
            boxSizing: 'border-box',
            direction,
          }}
        >
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 10,
              marginTop: -10,
              direction,
            }}
          >
            <div
              style={{
                width: 30,
                height: 30,
                borderRadius: '50%',
                overflow: 'hidden',
                border: '2px solid #E5E7EB',
                flexShrink: 0,
              }}
            >
              {user?.photoURL ? (
                <img
                  src={user.photoURL}
                  alt={greetingName}
                  style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                  onError={(e) => {
                    (e.currentTarget as HTMLImageElement).style.display = 'none';
                  }}
                />
              ) : (
                <div
                  style={{
                    width: '100%',
                    height: '100%',
                    background: BLUE_ACCENT,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: 'white',
                    fontWeight: 700,
                    fontSize: 14,
                  }}
                >
                  {initials(user?.displayName ?? user?.name ?? null)}
                </div>
              )}
            </div>
            <div style={{ fontSize: 22, fontWeight: 800, color: TEXT_DARK }}>
              {t('user_portal_greeting', 'שלום')}, {greetingName}!
            </div>
          </div>
          <div style={{ marginTop: 50 }}>
            <MyParashaSection
              parashaLabel={parashaLabel}
              progressPercent={progressPercent}
              masteredCount={masteredCount}
              practiceMinutes={practiceMinutes}
              direction={direction}
              traditionKey={traditionKey}
              targetDate={barMitzvaDate}
              barMitzvaDateLabel={barMitzvaDateLabel}
              hebrewDateLabel={hebrewDateLabel}
              haftaraLabel={haftaraTitle}
            />
          </div>

          <section
            role="button"
            tabIndex={0}
            onClick={handlePractice}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                handlePractice();
              }
            }}
            style={{
              background: BLUE_ACCENT,
              color: 'white',
              borderRadius: 24,
              padding: '14px 14px',
              marginTop: 40,
              height: 115,
              boxShadow: '0 20px 36px rgba(59, 123, 244, 0.35)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 4,
              direction: 'ltr',
              cursor: 'pointer',
              overflow: 'visible',
              position: 'relative',
            }}
          >
            {/* Boy with headphones – always on the LEFT, flows over top and bottom */}
            <div
              style={{
                marginBottom: 50,
                width: 115,
                height: 165,
                zIndex: 1,
              }}
              aria-hidden="true"
            >
              <img
                src={student1Img}
                alt=""
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                  objectPosition: 'center bottom',
                }}
              />
            </div>
            {/* Text + button – always on the RIGHT */}
            <div
              style={{
                flex: 1,
                minWidth: 0,
                display: 'flex',
                flexDirection: 'column',
                gap: 4,
                justifyContent: 'center',
                direction: isHebrew ? 'rtl' : 'ltr',
                textAlign: 'center',
              }}
            >
              <div className="text-xl" style={{ fontWeight: 800, lineHeight: 1.2 }}>
                {t('user_portal_start_practice', 'Start Practice')}
              </div>
              <div
                style={{
                  color: 'rgba(255,255,255,0.9)',
                  fontSize: 14,
                  lineHeight: 1.3,
                }}
              >
                {t('user_portal_continue', 'Continue where you left off')}
              </div>
            </div>
            {/* Yellow waveform icon – on the right */}
            <div
              style={{
                width: 60,
                height: 60,
                flexShrink: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
              aria-hidden="true"
            >
              <img
                src={playWaveIcon}
                alt=""
                style={{ width: 56, height: 56, objectFit: 'contain' }}
              />
            </div>
          </section>

          <TrueFactsCarousel />

          <SelfTestCard />

          {/* Family card */}
          <section
            role="button"
            tabIndex={0}
            onClick={() => navigate('/choose-parent-child')}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                navigate('/choose-parent-child');
              }
            }}
            style={{
              overflow: 'visible',
              position: 'relative',
              display: 'flex',
              alignItems: 'center',
              gap: 12,
              padding: '12px 16px',
              background: '#E8EDF5',
              borderRadius: 20,
              cursor: 'pointer',
              marginTop: 16,
              height: 100,
              direction,
            }}
          >
            <div style={{ fontSize: 20, fontWeight: 900, color: TEXT_SECONDARY, flexShrink: 0 }}>
              ‹
            </div>
            <div style={{ width: 200 }}>
              <p className="text-xl" style={{ margin: 0, fontWeight: 700, color: TEXT_DARK }}>
                {t('menu_family_card_title', 'המשפחה שלי')}
              </p>
              <p
                style={{
                  fontWeight: 700,
                  margin: '4px 0 0',
                  fontSize: 13,
                  color: TEXT_SECONDARY,
                  lineHeight: 1.4,
                }}
              >
                {t('menu_family_card_subtitle', 'חבר הורה או ילד כדי לעקוב אחר ההתקדמות')}
              </p>
            </div>
            <img
              src={familyImage}
              alt=""
              style={{
                height: '105px',
                flexShrink: 0,
                position: 'absolute',
                left: 0,
                bottom: 0,
              }}
            />
          </section>

          <AcademyCarousel />

          {/* Quick-access rows */}
          {/*<div style={{ display: "flex", flexDirection: "column", gap: 10, marginTop: 16 }}>*/}
          {/*  /!* קריאת שמע *!/*/}
          {/*  <section*/}
          {/*    role="button"*/}
          {/*    tabIndex={0}*/}
          {/*    onClick={() => navigate("/shema")}*/}
          {/*    onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); navigate("/shema"); } }}*/}
          {/*    style={{*/}
          {/*      background: "#ffffff", borderRadius: 16, padding: "14px 18px",*/}
          {/*      border: `1px solid ${BORDER_LIGHT}`, boxShadow: "0 2px 8px rgba(0,0,0,0.06)",*/}
          {/*      cursor: "pointer", display: "flex", alignItems: "center",*/}
          {/*      justifyContent: "space-between", gap: 12, direction,*/}
          {/*    }}*/}
          {/*  >*/}
          {/*    <div style={{ width: 40, height: 40, borderRadius: 10, background: "rgba(59,123,244,0.08)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 20, flexShrink: 0 }}>*/}
          {/*      📜*/}
          {/*    </div>*/}
          {/*    <span style={{ flex: 1, fontSize: 16, fontWeight: 700, color: TEXT_DARK, paddingInlineStart: 4 }}>*/}
          {/*      {t("user_portal_shema_section", "קריאת שמע")}*/}
          {/*    </span>*/}
          {/*    <span style={{ fontSize: 18, color: TEXT_SECONDARY }}>{isHebrew ? "←" : "→"}</span>*/}
          {/*  </section>*/}

          {/*  /!* ברכות התורה *!/*/}
          {/*  <section*/}
          {/*    role="button"*/}
          {/*    tabIndex={0}*/}
          {/*    onClick={() => navigate("/birchot-hatorah")}*/}
          {/*    onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); navigate("/birchot-hatorah"); } }}*/}
          {/*    style={{*/}
          {/*      background: "#ffffff", borderRadius: 16, padding: "14px 18px",*/}
          {/*      border: `1px solid ${BORDER_LIGHT}`, boxShadow: "0 2px 8px rgba(0,0,0,0.06)",*/}
          {/*      cursor: "pointer", display: "flex", alignItems: "center",*/}
          {/*      justifyContent: "space-between", gap: 12, direction,*/}
          {/*    }}*/}
          {/*  >*/}
          {/*    <div style={{ width: 40, height: 40, borderRadius: 10, background: "rgba(59,123,244,0.08)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 20, flexShrink: 0 }}>*/}
          {/*      📖*/}
          {/*    </div>*/}
          {/*    <span style={{ flex: 1, fontSize: 16, fontWeight: 700, color: TEXT_DARK, paddingInlineStart: 4 }}>*/}
          {/*      {t("user_portal_birchot_hatorah_section", "ברכות התורה")}*/}
          {/*    </span>*/}
          {/*    <span style={{ fontSize: 18, color: TEXT_SECONDARY }}>{isHebrew ? "←" : "→"}</span>*/}
          {/*  </section>*/}
          {/*</div>*/}

          {/* Gold aliyah banner */}
          {/* <section
          role="button"
          tabIndex={0}
          onClick={() => navigate("/aliya-latora")}
          onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); navigate("/aliya-latora"); } }}
          style={{
            background: "linear-gradient(135deg, #F5C842 0%, #E8A800 100%)",
            borderRadius: 24, padding: "18px 20px", marginTop: 16,
            minHeight: 100, boxShadow: "0 16px 32px rgba(245,200,66,0.4)",
            display: "flex", alignItems: "center", justifyContent: "space-between",
            gap: 12, direction: "ltr", cursor: "pointer", overflow: "visible", position: "relative",
          }}
        >
          <div style={{ position: "absolute", left: 0, bottom: 0, width: 110, height: 145, zIndex: 1 }} aria-hidden="true">
            <img src={yamen1Img} alt="" style={{ width: "100%", height: "100%", objectFit: "contain", objectPosition: "center bottom" }} />
          </div>
          <div style={{ flex: 1, paddingLeft: 110, direction: isHebrew ? "rtl" : "ltr", textAlign: isHebrew ? "right" : "left" }}>
            <div style={{ fontSize: 16, fontWeight: 800, color: TEXT_DARK, marginBottom: 4, lineHeight: 1.3 }}>
              {t("aliya_banner_subtitle", "עליה שישית-נוסח תימן")}
            </div>
            <div style={{ fontSize: 22, fontWeight: 800, color: "white", lineHeight: 1.2 }}>
              שוהם שימחי
            </div>
          </div>
          <div style={{ width: 36, height: 36, borderRadius: "50%", background: "rgba(255,255,255,0.35)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 20, flexShrink: 0, color: TEXT_DARK, fontWeight: 700 }} aria-hidden="true">
            ›
          </div>
        </section> */}

          {/* <section style={{ marginTop: 20 }}>
          {aliyaDate ? (
            <CountdownClock
              targetDate={aliyaDate}
              direction={direction}
              onClick={() => navigate("/aliya-latora")}
            />
          ) : (
            <div
              role="button"
              tabIndex={0}
              onClick={() => navigate("/aliya-latora")}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  navigate("/aliya-latora");
                }
              }}
              style={{
                background: "linear-gradient(135deg, #132a49, #1e3a5f)",
                borderRadius: 16,
                padding: "14px 18px",
                color: "white",
                boxShadow: "0 12px 24px rgba(15,31,56,0.3)",
                direction,
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 12,
              }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                <div style={{ fontSize: 24 }}>📅</div>
                <div style={{ fontSize: 16, fontWeight: 700 }}>
                  {t("countdown_set_date_title", "Set Your Birth Date")}
                </div>
              </div>
              <div
                style={{
                  background: "linear-gradient(135deg, #F5C842, #D4A832)",
                  color: "#1A1A2E",
                  padding: "8px 16px",
                  borderRadius: 10,
                  fontWeight: 700,
                  fontSize: 14,
                }}
              >
                {t("countdown_set_date_cta", "Set Date →")}
              </div>
            </div>
          )}
        </section> */}

          {/* תיקון קורים – opens tikun korim PDF for selected parasha */}
          {/*<section*/}
          {/*  role="button"*/}
          {/*  tabIndex={0}*/}
          {/*  onClick={() => navigate("/tikun-korim")}*/}
          {/*  onKeyDown={(e) => {*/}
          {/*    if (e.key === "Enter" || e.key === " ") {*/}
          {/*      e.preventDefault();*/}
          {/*      navigate("/tikun-korim");*/}
          {/*    }*/}
          {/*  }}*/}
          {/*  style={{*/}
          {/*    marginTop: 26,*/}
          {/*    background: "#ffffff",*/}
          {/*    borderRadius: 20,*/}
          {/*    padding: "18px",*/}
          {/*    boxShadow: "0 8px 24px rgba(0,0,0,0.1)",*/}
          {/*    border: `1px solid ${BORDER_LIGHT}`,*/}
          {/*    cursor: "pointer",*/}
          {/*    display: "flex",*/}
          {/*    alignItems: "center",*/}
          {/*    justifyContent: "space-between",*/}
          {/*    gap: 12,*/}
          {/*    direction,*/}
          {/*  }}*/}
          {/*>*/}
          {/*  <span style={{ fontSize: 20, fontWeight: 700, color: TEXT_DARK }}>*/}
          {/*    {t("user_portal_tikun_korim_section", "Tikun Korim")}*/}
          {/*  </span>*/}
          {/*  <span style={{ fontSize: 18, color: TEXT_SECONDARY }}>{isHebrew ? "←" : "→"}</span>*/}
          {/*</section>*/}

          {/* My Rabbi card */}
          {/*<section*/}
          {/*  role="button"*/}
          {/*  tabIndex={0}*/}
          {/*  onClick={handleAskRabbi}*/}
          {/*  onKeyDown={(e) => {*/}
          {/*    if (e.key === "Enter" || e.key === " ") {*/}
          {/*      e.preventDefault();*/}
          {/*      handleAskRabbi();*/}
          {/*    }*/}
          {/*  }}*/}
          {/*  style={{*/}
          {/*    marginTop: 26,*/}
          {/*    background: "#ffffff",*/}
          {/*    borderRadius: 20,*/}
          {/*    boxShadow: "0 8px 24px rgba(0,0,0,0.08)",*/}
          {/*    border: `1px solid ${BORDER_LIGHT}`,*/}
          {/*    cursor: "pointer",*/}
          {/*    display: "flex",*/}
          {/*    alignItems: "center",*/}
          {/*    gap: 16,*/}
          {/*    padding: "14px 18px",*/}
          {/*    direction,*/}
          {/*  }}*/}
          {/*>*/}
          {/*  /!* Rabbi avatar *!/*/}
          {/*  <div*/}
          {/*    style={{*/}
          {/*      flexShrink: 0,*/}
          {/*      width: 64,*/}
          {/*      height: 64,*/}
          {/*      borderRadius: "50%",*/}
          {/*      overflow: "hidden",*/}
          {/*      border: `2px solid ${BLUE_ACCENT}`,*/}
          {/*    }}*/}
          {/*    aria-hidden="true"*/}
          {/*  >*/}
          {/*    {rabbiAvatar()}*/}
          {/*  </div>*/}

          {/*  /!* Text *!/*/}
          {/*  <div style={{ flex: 1, minWidth: 0 }}>*/}
          {/*    <div style={{ fontSize: 18, fontWeight: 800, color: TEXT_DARK, lineHeight: 1.2 }}>*/}
          {/*      {rabbiNameDisplay}*/}
          {/*    </div>*/}
          {/*    <div style={{ fontSize: 13, color: TEXT_SECONDARY, marginTop: 4 }}>*/}
          {/*      {t("user_portal_rabbi_subtitle", "השאר בקשר עם המנטור שלך")}*/}
          {/*    </div>*/}
          {/*  </div>*/}

          {/*  /!* Arrow *!/*/}
          {/*  <span style={{ fontSize: 18, color: TEXT_SECONDARY, flexShrink: 0 }}>*/}
          {/*    {isHebrew ? "←" : "→"}*/}
          {/*  </span>*/}
          {/*</section>*/}

          {/* Review Requests Status */}
          {reviewRequests.length > 0 && (
            <>
              <div
                style={{
                  textAlign: 'center',
                  fontSize: 22,
                  fontWeight: 800,
                  marginTop: 26,
                  marginBottom: 10,
                  color: TEXT_DARK,
                }}
              >
                {t('my_review_requests', 'My Review Requests')}
              </div>

              <section
                style={{
                  background: '#ffffff',
                  color: TEXT_DARK,
                  borderRadius: 20,
                  padding: '18px',
                  boxShadow: '0 8px 24px rgba(0,0,0,0.08)',
                  border: `1px solid ${BORDER_LIGHT}`,
                  direction,
                }}
              >
                <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                  {reviewRequests.slice(0, 5).map((request) => {
                    const isPending = request.status === 'pending';
                    const isApproved = request.status === 'approved';
                    const isRejected = request.status === 'rejected';

                    return (
                      <div
                        key={request.id}
                        onClick={() => !isPending && setSelectedReview(request)}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 12,
                          padding: '12px 14px',
                          background: isPending
                            ? '#fefce8'
                            : isApproved
                              ? '#f0fdf4'
                              : isRejected
                                ? '#fef2f2'
                                : '#f3f4f6',
                          borderRadius: 14,
                          border: `1px solid ${
                            isPending
                              ? '#fef08a'
                              : isApproved
                                ? '#bbf7d0'
                                : isRejected
                                  ? '#fecaca'
                                  : '#e5e7eb'
                          }`,
                          cursor: !isPending ? 'pointer' : 'default',
                        }}
                      >
                        <div
                          style={{
                            width: 40,
                            height: 40,
                            borderRadius: '50%',
                            background: isPending
                              ? 'linear-gradient(135deg, #fbbf24, #f59e0b)'
                              : isApproved
                                ? 'linear-gradient(135deg, #22c55e, #16a34a)'
                                : isRejected
                                  ? 'linear-gradient(135deg, #ef4444, #dc2626)'
                                  : 'linear-gradient(135deg, #6b7280, #4b5563)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: 'white',
                            fontSize: 18,
                          }}
                        >
                          {isPending ? '⏳' : isApproved ? '✓' : '↻'}
                        </div>
                        <div style={{ flex: 1 }}>
                          <div
                            style={{
                              fontSize: 14,
                              fontWeight: 700,
                              color: '#111827',
                              marginBottom: 2,
                            }}
                          >
                            {request.parashaHebrew || request.parashaKey} · {t('verse', 'Verse')}{' '}
                            {formatNumberForLocale(request.verseIndex + 1, i18n.language || '')}
                          </div>
                          <div
                            style={{
                              fontSize: 12,
                              color: isPending
                                ? '#92400e'
                                : isApproved
                                  ? '#166534'
                                  : isRejected
                                    ? '#991b1b'
                                    : '#6b7280',
                            }}
                          >
                            {isPending
                              ? t('waiting_for_rabbi', "Waiting for Rabbi's feedback")
                              : isApproved
                                ? t('approved_by_rabbi', 'Approved by Rabbi!')
                                : t('needs_more_practice', 'Needs more practice')}
                          </div>
                        </div>
                        {!isPending && (
                          <div style={{ fontSize: 18, color: '#9ca3af' }}>
                            {isHebrew ? '←' : '→'}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </section>
            </>
          )}
        </div>
      </div>

      {/* Review Detail Modal */}
      {selectedReview && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 50,
            padding: 16,
          }}
          onClick={() => setSelectedReview(null)}
        >
          <div
            style={{
              background: 'white',
              borderRadius: 20,
              padding: 24,
              maxWidth: 400,
              width: '100%',
              boxShadow: '0 20px 40px rgba(0,0,0,0.2)',
              direction,
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                marginBottom: 16,
              }}
            >
              <h3
                style={{
                  fontSize: 18,
                  fontWeight: 800,
                  color: '#111827',
                }}
              >
                {t('rabbi_feedback', "Rabbi's Feedback")}
              </h3>
              <button
                onClick={() => setSelectedReview(null)}
                style={{
                  background: 'none',
                  border: 'none',
                  fontSize: 24,
                  color: '#9ca3af',
                  cursor: 'pointer',
                }}
              >
                ×
              </button>
            </div>

            {/* Status badge */}
            <div
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 6,
                padding: '6px 12px',
                borderRadius: 20,
                fontSize: 13,
                fontWeight: 600,
                marginBottom: 16,
                background: selectedReview.status === 'approved' ? '#dcfce7' : '#fee2e2',
                color: selectedReview.status === 'approved' ? '#166534' : '#991b1b',
              }}
            >
              {selectedReview.status === 'approved' ? '✓ ' : '↻ '}
              {selectedReview.status === 'approved'
                ? t('approved', 'Approved')
                : t('needs_work', 'Needs Work')}
            </div>

            {/* Verse */}
            <div
              style={{
                background: '#f8f9fa',
                borderRadius: 12,
                padding: 14,
                marginBottom: 16,
              }}
              dir="rtl"
            >
              <div
                style={{
                  fontSize: 10,
                  color: '#6b7280',
                  marginBottom: 6,
                  textTransform: 'uppercase',
                  letterSpacing: 0.5,
                }}
              >
                {selectedReview.parashaHebrew || selectedReview.parashaKey} · {t('verse', 'Verse')}{' '}
                {formatNumberForLocale(selectedReview.verseIndex + 1, i18n.language || '')}
              </div>
              <div
                style={{
                  fontSize: 16,
                  fontWeight: 500,
                  color: '#111827',
                  lineHeight: 1.6,
                }}
              >
                {selectedReview.verseText}
              </div>
            </div>

            {/* Rabbi's response */}
            {selectedReview.rabbiResponse && (
              <div
                style={{
                  background: '#f0f9ff',
                  borderRadius: 12,
                  padding: 14,
                  marginBottom: 16,
                  borderLeft: '4px solid #3b82f6',
                }}
              >
                <div
                  style={{
                    fontSize: 12,
                    color: '#1e40af',
                    marginBottom: 6,
                    fontWeight: 600,
                  }}
                >
                  💬 {t('rabbi_message', "Rabbi's Message")}
                </div>
                <div
                  style={{
                    fontSize: 14,
                    color: '#1e3a5f',
                    lineHeight: 1.5,
                  }}
                >
                  {selectedReview.rabbiResponse}
                </div>
              </div>
            )}

            <button
              onClick={() => setSelectedReview(null)}
              style={{
                width: '100%',
                padding: '12px 16px',
                borderRadius: 12,
                background: BLUE_ACCENT,
                color: 'white',
                fontWeight: 700,
                fontSize: 14,
                border: 'none',
                cursor: 'pointer',
              }}
            >
              {t('close', 'Close')}
            </button>
          </div>
        </div>
      )}

      {showConnectionSuccess && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.45)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 60,
            padding: 24,
          }}
          onClick={() => setShowConnectionSuccess(false)}
        >
          <div
            style={{
              background: 'white',
              borderRadius: 24,
              padding: '32px 24px 24px',
              maxWidth: 320,
              width: '100%',
              textAlign: 'center',
              boxShadow: '0 20px 48px rgba(0,0,0,0.2)',
              direction,
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ fontSize: 52, marginBottom: 12 }}>🎉</div>
            <div style={{ fontSize: 20, fontWeight: 800, color: TEXT_DARK, marginBottom: 8 }}>
              {t('connection_success_title', 'Connected successfully!')}
            </div>
            {connectedName && (
              <div style={{ fontSize: 18, fontWeight: 700, color: BLUE_ACCENT, marginBottom: 8 }}>
                {connectedName}
              </div>
            )}
            <div style={{ fontSize: 14, color: TEXT_SECONDARY, lineHeight: 1.6, marginBottom: 20 }}>
              {t(
                'connection_success_body',
                'You are now connected and can track progress together.',
              )}
            </div>
            <button
              onClick={() => setShowConnectionSuccess(false)}
              style={{
                width: '100%',
                padding: '12px 16px',
                borderRadius: 14,
                background: BLUE_ACCENT,
                color: 'white',
                fontWeight: 700,
                fontSize: 15,
                border: 'none',
                cursor: 'pointer',
              }}
            >
              {t('close', 'Close')}
            </button>
          </div>
        </div>
      )}

      <BottomNav />
    </div>
  );
};

export default UserPortalPage;
